import {
  type MediaHouseMedia,
  TagMediaHouseDocument,
  type TagMediaHouseMutation,
  type TagMediaHouseMutationVariables,
  TranscribeMediaDocument,
  type TranscribeMediaMutation,
  type TranscribeMediaMutationVariables,
} from "@/generated/graphql";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FiInfo, FiLoader, FiX } from "react-icons/fi";

interface MediaPropertiesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  media: MediaHouseMedia;
  mediaType: "image" | "video" | "audio";
}

export function MediaPropertiesDialog({
  isOpen,
  onClose,
  media,
  mediaType,
}: MediaPropertiesDialogProps) {
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [transcription, setTranscription] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const showTranscribe = media.type === "audio" || media.type === "video";

  // Get the correct refetch query based on media type
  const getRefetchQuery = () => {
    if (mediaType === "image") return "GetMediaHouseImages";
    if (mediaType === "video") return "GetMediaHouseVideos";
    return "GetMediaHouseAudio";
  };

  const [updateTags, { loading: updatingTags }] = useMutation<
    TagMediaHouseMutation,
    TagMediaHouseMutationVariables
  >(TagMediaHouseDocument, {
    refetchQueries: [getRefetchQuery()],
    onCompleted: () => {
      toast.success("Tags updated successfully");
    },
    onError: (err) => {
      toast.error(`Failed to update tags: ${err.message}`);
    },
  });

  const [transcribeMedia] = useMutation<TranscribeMediaMutation, TranscribeMediaMutationVariables>(
    TranscribeMediaDocument,
    {
      onCompleted: (data) => {
        if (data.transcribeMedia.text) {
          setTranscription(data.transcribeMedia.text);
          toast.success("Transcription complete!");
        } else {
          toast.error("Transcription failed - no text returned");
        }
        setIsTranscribing(false);
      },
      onError: (err) => {
        if (!toastAiCredits(err, "transcribeMediaToast")) {
          toast.error(`Transcription failed: ${err.message}`);
        }
        setIsTranscribing(false);
      },
    }
  );

  // Initialize tags from media
  useEffect(() => {
    if (isOpen) {
      setTags(media.tags || []);
      setTranscription(media.audioText || null);
    }
  }, [isOpen, media]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setTagInput("");
      setTranscription(null);
    }
  }, [isOpen]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      const newTags = [...tags, tagInput.trim()];
      setTags(newTags);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleSaveTags = async () => {
    await updateTags({
      variables: {
        id: media.id,
        tags,
      },
    });
  };

  const handleTranscribe = async () => {
    setIsTranscribing(true);
    await transcribeMedia({
      variables: {
        mediaId: media.id,
      },
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDuration = (seconds: number | null | undefined): string => {
    if (!seconds) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="!mt-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="properties-title"
    >
      <div
        className="w-full max-w-2xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
          <div className="flex items-center gap-2">
            <FiInfo className="h-5 w-5 text-gray-600" />
            <h2 id="properties-title" className="h6">
              Media Properties
            </h2>
          </div>
          <IconButton
            onClick={onClose}
            variant="ghost"
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <FiX className="h-5 w-5" />
          </IconButton>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Media Preview */}
          <section>
            <label className="mb-2 block label">Preview</label>
            <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
              {media.type === "image" && (
                <img src={media.url} alt={media.filename} className="max-h-64 mx-auto rounded" />
              )}
              {media.type === "video" && (
                <video src={media.url} controls className="max-h-64 mx-auto rounded">
                  <track kind="captions" />
                </video>
              )}
              {media.type === "audio" && (
                <audio src={media.url} controls className="w-full">
                  <track kind="captions" />
                </audio>
              )}
            </div>
          </section>

          {/* File Info */}
          <section>
            <label className="mb-2 block label">File Information</label>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Filename:</span>
                <span className="font-medium text-secondary-200">{media.filename}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Type:</span>
                <span className="font-medium text-secondary-200 capitalize">{media.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Size:</span>
                <span className="font-medium text-secondary-200">{formatFileSize(media.size)}</span>
              </div>
              {media.duration && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-medium text-secondary-200">
                    {formatDuration(media.duration)}
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* Transcription */}
          {showTranscribe && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <label className="label">Transcription</label>
                <Button
                  onClick={handleTranscribe}
                  disabled={isTranscribing}
                  variant="ghost"
                  size="sm"
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {isTranscribing && <FiLoader className="animate-spin" />}
                  {isTranscribing ? "Transcribing..." : "Generate Transcription"}
                </Button>
              </div>
              {transcription ? (
                <div className="rounded-md border border-gray-200 p-3 bg-gray-50 text-sm">
                  {transcription}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  No transcription available. Click "Generate Transcription" to create one.
                </p>
              )}
            </section>
          )}

          {/* Tags */}
          <section>
            <label className="mb-2 block label">Tags</label>
            <div className="space-y-2">
              {/* Tag input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Add a tag..."
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <Button onClick={handleAddTag} variant="primary" size="md">
                  Add
                </Button>
              </div>

              {/* Tag list */}
              {tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-800"
                    >
                      {tag}
                      <IconButton
                        onClick={() => handleRemoveTag(tag)}
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove tag ${tag}`}
                        className="h-4 w-4 p-0 hover:text-primary-900"
                      >
                        <FiX className="h-3 w-3" />
                      </IconButton>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No tags added yet</p>
              )}

              {/* Save tags button */}
              <Button
                onClick={handleSaveTags}
                disabled={updatingTags}
                variant="primary"
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {updatingTags ? "Saving..." : "Save Tags"}
              </Button>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t bg-white px-6 py-4">
          <Button onClick={onClose} variant="secondary" className="w-full">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
